import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { request } from '../api/client';
import type {
  CreateCustomerRequest,
  CustomerList,
  CustomerRow,
  InvitationIssued,
  SortKey,
} from '../api/types';
import { day, money, since } from '../components/format';
import { Failed, Modal, PageHead, Pill, TokenReveal } from '../components/ui';

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: 'name', label: 'Customer' },
  { key: 'status', label: 'Status' },
  { key: 'revenue', label: 'Billed MTD', numeric: true },
  { key: 'cost', label: 'Cost MTD', numeric: true },
  { key: 'last_bill', label: 'Last month' },
  { key: 'last_active', label: 'Last active' },
  { key: 'created', label: 'Created' },
  { key: 'failures', label: 'Failures 24h', numeric: true },
];

export function Customers() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [descending, setDescending] = useState(false);
  const [params, setParams] = useSearchParams();
  const creating = params.get('new') === '1';

  const query = new URLSearchParams({ sort, descending: String(descending) });
  if (search.trim()) query.set('search', search.trim());
  if (status) query.set('status', status);

  const { data, isPending, error } = useQuery({
    queryKey: ['customers', query.toString()],
    queryFn: () => request<CustomerList>(`/api/v1/admin/customers?${query.toString()}`),
    // Keeps the table on screen while a new sort or search is in flight, so the page does not
    // blink back to "Loading…" every time a key is pressed in the search box.
    placeholderData: keepPreviousData,
  });

  function toggle(key: SortKey) {
    if (key === sort) {
      setDescending((current) => !current);
      return;
    }
    setSort(key);
    // Money, activity and failures are nearly always wanted biggest-first; a name is not.
    setDescending(key !== 'name' && key !== 'status');
  }

  return (
    <>
      <PageHead
        eyebrow="Customers"
        title="Every organisation"
        note="Closed accounts included: who we lost is a question this list has to answer."
        actions={
          <button type="button" className="button" onClick={() => setParams({ new: '1' })}>
            New customer
          </button>
        }
      />

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name, owner or email…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ minWidth: 280 }}
          aria-label="Search customers"
        />
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="suspended">Suspended</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {error ? <Failed error={error} fallback="Could not load customers." /> : null}

      <div className="panel">
        {isPending || !data ? (
          <div className="panel-body muted">Loading…</div>
        ) : data.customers.length === 0 ? (
          <div className="panel-body muted">No customer matches that.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th key={column.key} className={column.numeric ? 'numeric' : undefined}>
                      <button
                        type="button"
                        onClick={() => toggle(column.key)}
                        aria-label={`Sort by ${column.label}`}
                      >
                        {column.label}
                        {sort === column.key ? <span>{descending ? '↓' : '↑'}</span> : null}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.customers.map((row) => (
                  <Row key={row.organisation_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? <CreateCustomer onClose={() => setParams({})} /> : null}
    </>
  );
}

function Row({ row }: { row: CustomerRow }) {
  return (
    <tr>
      <td>
        <Link to={`/customers/${row.organisation_id}`}>{row.name}</Link>
        {row.owner_email ? <div className="muted small">{row.owner_email}</div> : null}
      </td>
      <td>
        <Pill value={row.account_status} />
      </td>
      <td className="numeric">{money(row.revenue_month_to_date_gbp)}</td>
      <td className="numeric muted">{money(row.marginal_cost_gbp)}</td>
      <td>
        <LastBill row={row} />
      </td>
      <td className={row.last_active_at ? undefined : 'muted'}>{since(row.last_active_at)}</td>
      <td className="muted">{day(row.created_at)}</td>
      <td className={`numeric ${row.failures_24h ? 'bad' : 'muted'}`}>{row.failures_24h || '—'}</td>
    </tr>
  );
}

/**
 * Last month's bill, at a glance. "Paid · stripe" is the one this column is for: a customer pays
 * in the app, Stripe tells the server, and the next time this page loads it says so without
 * anyone opening a terminal.
 */
function LastBill({ row }: { row: CustomerRow }) {
  if (!row.last_bill_state) return <span className="faint">—</span>;
  const amount = money(row.last_bill_gbp);
  if (row.last_bill_state === 'paid') {
    return (
      <span className="good">
        Paid {amount}
        {row.last_bill_via ? <span className="muted"> · {row.last_bill_via}</span> : null}
      </span>
    );
  }
  if (row.last_bill_state === 'sent') {
    return <span className="warn">Sent {amount}, unconfirmed</span>;
  }
  return <span className="bad">Owed {amount}</span>;
}

/** The onboarding CLI's `create`, as a form. The token comes back once and is shown once. */
export function CreateCustomer({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const [form, setForm] = useState({
    email: '',
    organisation_name: '',
    plan_code: 'beta',
    valid_days: 7,
    display_name: '',
    trading_description: '',
    owner_first_name: '',
    reply_number: '',
    sms_sender_id: '',
    billing_reference_prefix: '',
  });
  const [issued, setIssued] = useState<InvitationIssued | null>(null);

  const create = useMutation({
    mutationFn: (body: CreateCustomerRequest) =>
      request<InvitationIssued>('/api/v1/admin/customers', { method: 'POST', body }),
    onSuccess: (result) => {
      setIssued(result);
      void client.invalidateQueries({ queryKey: ['customers'] });
      void client.invalidateQueries({ queryKey: ['overview'] });
    },
  });

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const blank = (value: string) => (value.trim() ? value.trim() : null);
    create.mutate({
      email: form.email.trim(),
      organisation_name: form.organisation_name.trim(),
      plan_code: form.plan_code.trim() || 'beta',
      valid_days: form.valid_days,
      display_name: blank(form.display_name),
      trading_description: blank(form.trading_description),
      owner_first_name: blank(form.owner_first_name),
      reply_number: blank(form.reply_number),
      sms_sender_id: blank(form.sms_sender_id),
      billing_reference_prefix: blank(form.billing_reference_prefix),
    });
  }

  if (issued) {
    return (
      <TokenReveal
        email={issued.email}
        organisation={issued.organisation_name}
        token={issued.token}
        expires={day(issued.expires_at)}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal title="New customer" onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        Creates the organisation and an invitation for its owner. Everything past the first two
        fields can be set later.
      </p>
      <form onSubmit={submit}>
        {create.error ? (
          <Failed error={create.error} fallback="Could not create the customer." />
        ) : null}
        <div className="field-grid">
          <label className="field">
            <span>Owner email</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              autoFocus
            />
          </label>
          <label className="field">
            <span>Organisation</span>
            <input
              type="text"
              required
              value={form.organisation_name}
              onChange={(e) => set('organisation_name', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Trading name shown to their customers</span>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => set('display_name', e.target.value)}
              placeholder="Same as organisation"
            />
          </label>
          <label className="field">
            <span>Trading description</span>
            <input
              type="text"
              value={form.trading_description}
              onChange={(e) => set('trading_description', e.target.value)}
              placeholder="an electrical contractor"
            />
          </label>
          <label className="field">
            <span>Owner first name</span>
            <input
              type="text"
              value={form.owner_first_name}
              onChange={(e) => set('owner_first_name', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Reply number (E.164)</span>
            <input
              type="text"
              value={form.reply_number}
              onChange={(e) => set('reply_number', e.target.value)}
              placeholder="+447700900123"
            />
          </label>
          <label className="field">
            <span>SMS sender ID</span>
            <input
              type="text"
              value={form.sms_sender_id}
              onChange={(e) => set('sms_sender_id', e.target.value)}
              maxLength={11}
            />
          </label>
          <label className="field">
            <span>Billing reference prefix</span>
            <input
              type="text"
              value={form.billing_reference_prefix}
              onChange={(e) => set('billing_reference_prefix', e.target.value)}
              maxLength={8}
            />
          </label>
          <label className="field">
            <span>Plan</span>
            <input
              type="text"
              value={form.plan_code}
              onChange={(e) => set('plan_code', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Invitation valid for (days)</span>
            <input
              type="number"
              min={1}
              max={30}
              value={form.valid_days}
              onChange={(e) => set('valid_days', Number(e.target.value))}
            />
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="button-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create and invite'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
